import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import ContentLoader from '../../components/ContentLoader';
import { SmeChatSuggestionPanel } from '../../components/sme/SmeChatSuggestionPanel';
import { Switch } from '../../components/ui';
import { CHAT_LIVE_POLL_FAST_MS, useChatLivePoll } from '../../hooks/useChatLivePoll';
import { getAssessmentDetails, getSubCapabilityAssessmentData } from '../../services/assessment';
import { fetchChat, handleSendMessage, downloadChatFile, getChatCount, getSmeChatMode, getSuggestedChatQuestions, setSmeChatAuto, sendInterviewQuestion, submitInterviewClientReply, refreshInterviewSuggestion, syncSuggesterUnused } from '../../services/chatApi';
import { fetchSelectedCapabilities } from '../../services/capabilitiesApi';
import { useAuth } from '../../auth/AuthContext';
import {
    getThreadMessagesNewestFirst,
    getUnreadActionableMessages,
    hasUnreadForCapability,
    hasUnreadForSubCapability,
    isNotificationUnread,
    markChatThreadViewed,
} from '../../utils/chatReadState';
import {
    assessmentUnitKey,
    isCapabilityNavHighlighted,
    isLeafSelectedCapability,
    parseAssessmentUnitKey,
    sameAssessmentUnit,
} from '../../utils/assessmentUnitUtils';
import {
    chatApiSubCapabilityId,
    chatMessageStorageKey,
    remapUnusedSuggestionsFromApi,
} from '../../utils/chatThreadUtils';
import "../../assets/css/chat.css";
import "../../styles/smechat.css";
const Chat = ({
    project,
    clientUserId,
    currentUserId,
    readOnly = false,
    isTabActive = true,
    prefetchedChatHistory = null,
}) => {
    const basename = process.env.PUBLIC_URL || '';
    const { user } = useAuth();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chatHistory, setChatHistory] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showModals, setShowModals] = useState([]);
    const [projectId, setProjectId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [messages, setMessages] = useState({});
    const [filesBySubCapability, setFilesBySubCapability] = useState({});
    const [isDisabled, setIsDisabled] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);
    const activeSubCapabilityRef = useRef(null);
    const [currentState, setCurrentState] = useState('');
    const [futureState, setFutureState] = useState('');
    const [currentStatusDescription, setCurrentStatusDescription] = useState('');
    const [futureStatusDescription, setFutureStatusDescription] = useState('');
    const [showAssessmentModal, setShowAssessmentModal] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [subSelectedCard, setSubSelectedCard] = useState(null);
    const [expandedCapability, setExpandedCapability] = useState(null);
    const [latestMessage, setLatestMessage] = useState(null);
    const [readStateVersion, setReadStateVersion] = useState(0);
    const [selectedSubCapability, setSelectedSubCapability] = useState('all');
    const dropdownRefs = useRef({});
    const autoOpenTimerRef = useRef(null);
    const userOpenedChatRef = useRef(false);
    const body = document.body;
    const [selectedCapability, setSelectedCapability] = useState('all');
    const [showCapabilitiesModal, setShowCapabilitiesModal] = useState(false);
    const [selectedCapabilitiesData, setSelectedCapabilitiesData] = useState(null);
    const [suggestedQuestions, setSuggestedQuestions] = useState({});
    const [conversationIds, setConversationIds] = useState({});
    const [auto, setAuto] = useState(null);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestionPanelLoading, setSuggestionPanelLoading] = useState(false);
    // Suggestions copied into the composer, per thread, so the side panel can render them as consumed.
    const [usedSuggestions, setUsedSuggestions] = useState({});
    const [suggestionSources, setSuggestionSources] = useState({});
    const composerRef = useRef(null);
    const syncedClientMessageIdsRef = useRef({});
    const suggestionAttemptedRef = useRef({});
    const clientReplySyncInFlightRef = useRef({});
    const unusedSuggestionsRef = useRef({});
    const selectedCapabilitiesRef = useRef([]);
    const threadBodyRef = useRef(null);
    const threadScrollRef = useRef({ threadKey: null, stickToBottom: true });
    const THREAD_NEAR_BOTTOM_PX = 72;

    const scrollThreadToBottom = () => {
        const el = threadBodyRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    };

    const isThreadNearBottom = (el) =>
        el.scrollHeight - el.scrollTop - el.clientHeight <= THREAD_NEAR_BOTTOM_PX;

    const handleThreadBodyScroll = () => {
        const el = threadBodyRef.current;
        if (el) {
            threadScrollRef.current.stickToBottom = isThreadNearBottom(el);
        }
    };

    // All capability/sub-capability threads with unread client messages (not just the latest)
    const unreadThreads = useMemo(
        () => getUnreadActionableMessages(chatHistory, userId, projectId, "sme"),
        [chatHistory, userId, projectId, readStateVersion]
    );

    // Live message refresh while chat tab is open (SME + SU view); menu badges stay on ~30s poll.
    useChatLivePoll({
        userId,
        projectId,
        viewerRole: 'sme',
        setChatHistory,
        setLatestMessage,
        enabled: isTabActive && !loading && auto !== null,
        pollIntervalMs: auto === false ? CHAT_LIVE_POLL_FAST_MS : undefined,
    });

    useEffect(() => {
        if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = false;
            recognitionRef.current.lang = "en-US";

            recognitionRef.current.onresult = (event) => {
                const speechArray = Array.from(event.results);
                const finalTranscript = speechArray.map((result) => result[0].transcript).join("");
                const activeSubCapabilityId = activeSubCapabilityRef.current;
                if (activeSubCapabilityId) {
                    setMessages((prev) => ({
                        ...prev,
                        [activeSubCapabilityId]: finalTranscript,
                    }));
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error(`Speech recognition error: ${event.error}`);
                setIsListening(false);
            };
        } else {
            console.error("Speech-to-Text is not supported in this browser.");
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, []);

    // Use project.projectNumber or project.projectId from props
    useEffect(() => {
        if (project && (project.projectId)) {
            setProjectId(project.projectId);
        } else {
            // Fallback to localStorage only if no project prop
            const storedProjectId = localStorage.getItem('projectId');
            if (storedProjectId) {
                setProjectId(parseInt(storedProjectId));
            }
        }
    }, [project]);

    // Client user id drives assessment/chat APIs; SME id must not be used here.
    useEffect(() => {
        const resolvedClientId =
            clientUserId ??
            project?.userId ??
            project?.UserId ??
            null;

        if (resolvedClientId) {
            setUserId(resolvedClientId);
        } else if (currentUserId) {
            setUserId(currentUserId);
        } else if (user?.userId) {
            setUserId(user.userId);
        }
    }, [clientUserId, project, currentUserId, user]);

    const smeUserId = currentUserId ?? user?.userId;
    const isChatModuleReady = !loading && auto !== null;

    const threadStorageKey = (capabilityId, subCapabilityId) =>
        chatMessageStorageKey(capabilityId, subCapabilityId);

    const getLatestClientMessageForThread = (
        capabilityId,
        subCapabilityId,
        history = chatHistory
    ) =>
        (history || [])
            .filter(
                (chat) =>
                    sameAssessmentUnit(
                        chat.capabilityId,
                        chat.subCapabilityId,
                        capabilityId,
                        subCapabilityId
                    ) &&
                    chat.senderName !== "SME" &&
                    chat.id
            )
            .sort((a, b) => b.id - a.id)[0];

    const ensureSyncedClientBaseline = (capabilityId, subCapabilityId, history = chatHistory) => {
        const key = threadStorageKey(capabilityId, subCapabilityId);
        if (syncedClientMessageIdsRef.current[key] != null) {
            return;
        }
        const latestClient = getLatestClientMessageForThread(
            capabilityId,
            subCapabilityId,
            history
        );
        if (latestClient?.id) {
            syncedClientMessageIdsRef.current[key] = latestClient.id;
        }
    };

    const applyUnusedSuggestionsMap = (unusedBySubCapability) => {
        if (!unusedBySubCapability || typeof unusedBySubCapability !== 'object') {
            unusedSuggestionsRef.current = {};
            return;
        }

        const remapped = remapUnusedSuggestionsFromApi(
            unusedBySubCapability,
            selectedCapabilitiesRef.current
        );
        const next = {};
        Object.entries(remapped).forEach(([threadKey, questions]) => {
            next[threadKey] = dedupeSuggestions(questions);
        });
        unusedSuggestionsRef.current = next;
    };

    const applyInterviewSessionResult = (result) => {
        applyUnusedSuggestionsMap(result?.unusedBySubCapability);

        const nextQuestions = {};
        const nextConversationIds = {};
        (result?.questions || []).forEach((item) => {
            if (item?.capabilityId == null) {
                return;
            }
            const subCapKey = threadStorageKey(item.capabilityId, item.subCapabilityId);
            const fromApi = normalizeSuggestionsFromApi(item);
            const unused = unusedSuggestionsRef.current[subCapKey] || [];
            const list = dedupeSuggestions([...fromApi, ...unused]).slice(0, 4);
            if (list.length > 0) {
                nextQuestions[subCapKey] = list;
                if (fromApi.length > 0) {
                    setUnusedForSubCap(item.capabilityId, item.subCapabilityId, list);
                }
            }
            if (item.conversationId) {
                nextConversationIds[subCapKey] = item.conversationId;
            }
        });
        setSuggestedQuestions(nextQuestions);
        setConversationIds(nextConversationIds);
        syncedClientMessageIdsRef.current = {};
        suggestionAttemptedRef.current = {};
        clientReplySyncInFlightRef.current = {};
    };

    const clearUnusedSuggestions = () => {
        unusedSuggestionsRef.current = {};
    };

    const setUnusedForSubCap = (capabilityId, subCapabilityId, suggestions) => {
        const key = threadStorageKey(capabilityId, subCapabilityId);
        const normalized = dedupeSuggestions(suggestions);
        if (!normalized.length) {
            delete unusedSuggestionsRef.current[key];
        } else {
            unusedSuggestionsRef.current[key] = normalized;
        }

        if (!userId || !projectId || !smeUserId) {
            return;
        }

        void syncSuggesterUnused({
            userId,
            projectId,
            smeUserId,
            subCapabilityId: chatApiSubCapabilityId(subCapabilityId),
            questions: normalized,
        });
    };

    const normalizeSuggestionsFromApi = (item) => {
        const suggestions = item?.suggestions ?? item?.Suggestions;
        if (Array.isArray(suggestions) && suggestions.length > 0) {
            return suggestions.filter((q) => typeof q === "string" && q.trim());
        }
        const single = item?.question ?? item?.Question;
        if (single) {
            return [single];
        }
        return [];
    };

    const dedupeSuggestions = (list) => {
        const seen = new Set();
        return (list || []).filter((question) => {
            const text = String(question || "").trim();
            if (!text || seen.has(text)) {
                return false;
            }
            seen.add(text);
            return true;
        });
    };

    const mergeWithStoredUnused = (capabilityId, subCapabilityId, newSuggestions) => {
        const key = threadStorageKey(capabilityId, subCapabilityId);
        const unused = unusedSuggestionsRef.current[key] || [];
        return dedupeSuggestions([...(newSuggestions || []), ...unused]).slice(0, 4);
    };

    const stashUnusedSuggestions = (capabilityId, subCapabilityId, suggestions, usedContent) => {
        const content = String(usedContent || "").trim();
        const current = dedupeSuggestions(suggestions);
        const remaining = content && current.some((q) => q.trim() === content)
            ? current.filter((q) => q.trim() !== content)
            : current;
        setUnusedForSubCap(capabilityId, subCapabilityId, remaining);
        return remaining;
    };

    const getSuggestionsForThread = (capabilityId, subCapabilityId) => {
        const value = suggestedQuestions[threadStorageKey(capabilityId, subCapabilityId)];
        if (Array.isArray(value)) {
            return value;
        }
        if (typeof value === "string" && value.trim()) {
            return [value];
        }
        return [];
    };

    const chatInitStartedRef = useRef(false);

    // When projectId or userId changes (i.e. when the team member changes),
    // clear previous chat history and modal data before fetching new data.
    useEffect(() => {
        if (!projectId || !userId) {
            chatInitStartedRef.current = false;
            return;
        }

        if (!isTabActive && !chatInitStartedRef.current) {
            return;
        }

        chatInitStartedRef.current = true;

        userOpenedChatRef.current = false;
        setChatHistory([]);
        setShowModals([]);
        setShowModal(false);
        setData(null);
        setAuto(null);
        setSuggestedQuestions({});
        setConversationIds({});
        syncedClientMessageIdsRef.current = {};
        suggestionAttemptedRef.current = {};
        clientReplySyncInFlightRef.current = {};
        clearUnusedSuggestions();
        setSuggestionsLoading(false);
        setSuggestionPanelLoading(false);
        setLoading(true);

        let cancelled = false;
        let resolvedAuto = false;

        const initializeChatModule = async () => {
            try {
                const payload = { userId, projectId };
                const prefetched =
                    Array.isArray(prefetchedChatHistory) && prefetchedChatHistory.length > 0
                        ? prefetchedChatHistory
                        : null;

                const chatPromise = prefetched
                    ? Promise.resolve(prefetched)
                    : fetchChat(payload);

                const [chatData, capabilitiesData, modeResult] = await Promise.all([
                    chatPromise,
                    getAssessmentDetails(payload),
                    getSmeChatMode({
                        userId,
                        projectId,
                        smeUserId,
                    }),
                ]);

                if (!cancelled) {
                    try {
                        const selectedCapabilitiesResponse = await fetchSelectedCapabilities(payload);
                        if (!cancelled) {
                            setSelectedCapabilitiesData(selectedCapabilitiesResponse);
                        }
                    } catch (capabilitiesError) {
                        console.error(
                            "Error fetching selected capabilities:",
                            capabilitiesError?.message || capabilitiesError
                        );
                    }
                }

                if (cancelled) {
                    return;
                }

                selectedCapabilitiesRef.current =
                    capabilitiesData?.data?.selectedCapabilities ?? [];
                setData({ ...capabilitiesData });
                setChatHistory(chatData);

                const unreadActionable = getUnreadActionableMessages(
                    chatData,
                    userId,
                    projectId,
                    "sme"
                );

                if (unreadActionable.length > 0) {
                    const latestUnread = unreadActionable[0];
                    setLatestMessage({
                        capabilityId: latestUnread.capabilityId,
                        subCapabilityId: latestUnread.subCapabilityId,
                        messageDate: latestUnread.messageDate,
                    });
                    setExpandedCapability(latestUnread.capabilityId);
                    setSelectedCard(latestUnread.capabilityId);
                } else {
                    setLatestMessage(null);
                }

                resolvedAuto = modeResult?.auto === true;
                setAuto(resolvedAuto);
            } catch (error) {
                console.error("Error initializing SME chat:", error?.message || error);
                if (!cancelled) {
                    resolvedAuto = false;
                    setAuto(false);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    if (!resolvedAuto) {
                        void fetchSuggestedQuestions();
                    }
                }
            }
        };

        initializeChatModule();

        return () => {
            cancelled = true;
        };
    }, [projectId, userId, smeUserId, isTabActive]);

    useEffect(() => {
        return () => {
            body.classList.remove("modal-open");
        };
    }, []);

    useEffect(() => {
        if (!showModal || subSelectedCard == null || selectedCard == null || !userId || !projectId) {
            return;
        }

        const latestUserMessage = chatHistory
            .filter(
                (chat) =>
                    chat.senderName !== "SME" &&
                    sameAssessmentUnit(
                        chat.capabilityId,
                        chat.subCapabilityId,
                        selectedCard,
                        subSelectedCard
                    )
            )
            .sort((a, b) => new Date(b.messageDate) - new Date(a.messageDate))[0];

        if (latestUserMessage?.messageDate) {
            markChatThreadViewed(
                userId,
                projectId,
                selectedCard,
                subSelectedCard,
                latestUserMessage.messageDate
            );
            setReadStateVersion((version) => version + 1);
        }
    }, [showModal, selectedCard, subSelectedCard, chatHistory, userId, projectId]);

    useLayoutEffect(() => {
        if (!showModal || selectedCard == null || subSelectedCard == null) {
            return;
        }

        const threadKey = assessmentUnitKey(selectedCard, subSelectedCard);
        const threadChanged = threadScrollRef.current.threadKey !== threadKey;
        if (threadChanged) {
            threadScrollRef.current.threadKey = threadKey;
            threadScrollRef.current.stickToBottom = true;
        }

        if (threadScrollRef.current.stickToBottom) {
            scrollThreadToBottom();
        }
    }, [showModal, selectedCard, subSelectedCard, chatHistory]);

    // Show New badge briefly, then auto-open the unread chat after 5 seconds
    useEffect(() => {
        if (
            !latestMessage ||
            !userId ||
            !projectId ||
            auto === null ||
            !data?.data?.selectedCapabilities ||
            userOpenedChatRef.current
        ) {
            return;
        }

        if (
            !isNotificationUnread(
                userId,
                projectId,
                latestMessage.capabilityId,
                latestMessage.subCapabilityId,
                latestMessage.messageDate
            )
        ) {
            return;
        }

        if (autoOpenTimerRef.current) {
            clearTimeout(autoOpenTimerRef.current);
        }

        autoOpenTimerRef.current = setTimeout(() => {
            if (userOpenedChatRef.current) {
                return;
            }

            const capability = data.data.selectedCapabilities.find(
                (cap) => cap.capabilityId === latestMessage.capabilityId
            );
            const subCapability = capability?.selectedSubCapabilities.find((subCap) =>
                sameAssessmentUnit(
                    capability.capabilityId,
                    subCap.subCapabilityId,
                    latestMessage.capabilityId,
                    latestMessage.subCapabilityId
                )
            );

            setExpandedCapability(latestMessage.capabilityId);
            setSelectedCard(latestMessage.capabilityId);
            setSubSelectedCard(latestMessage.subCapabilityId);
            setShowModals([
                {
                    capabilityId: latestMessage.capabilityId,
                    subCapabilityId: latestMessage.subCapabilityId,
                    subCapability: subCapability?.subCapability,
                },
            ]);
            setShowModal(true);
        }, 5000);

        return () => {
            if (autoOpenTimerRef.current) {
                clearTimeout(autoOpenTimerRef.current);
                autoOpenTimerRef.current = null;
            }
        };
    }, [latestMessage, userId, projectId, data, auto]);

    const handleDownloadChat = () => {
        if (!chatHistory || chatHistory.length === 0) {
            alert('No chat history available.');
            return;
        }
    
        if (!data?.data?.selectedCapabilities || data.data.selectedCapabilities.length === 0) {
            console.error('Capabilities data is empty or undefined:', data?.data?.selectedCapabilities);
            alert('Capabilities data is unavailable.');
            return;
        }
    
        let filteredChats;
    
        if (selectedCapability === 'all') {
            const groupedChats = data.data.selectedCapabilities.map((capability) => {
                const capabilityChats = capability.selectedSubCapabilities.map((subCapability) => {
                    const subChats = chatHistory.filter(
                        (chat) =>
                            `${chat.capabilityId}` === `${capability.capabilityId}` &&
                            `${chat.subCapabilityId}` === `${subCapability.subCapabilityId}`
                    );
    
                    if (subChats.length > 0) {
                        return [
                            `Sub-Capability: ${subCapability.subCapability}`,
                            ...subChats.map(
                                (chat) =>
                                    `[${chat.senderName}] (${formatChatDate(new Date(chat.messageDate))}): ${chat.message}`
                            ),
                            '',
                        ].join('\n');
                    }
    
                    return '';
                });
    
                return [
                    `Capability: ${capability.capability}`,
                    ...capabilityChats.filter(Boolean),
                    '',
                ].join('\n');
            });
    
            filteredChats = groupedChats.filter(Boolean).join('\n');
        } else {
            const capability = data.data.selectedCapabilities.find(
                (cap) => `${cap.capabilityId}` === `${selectedCapability}`
            );
    
            const capabilityName = capability?.capability || 'Unknown Capability';
    
            if (selectedSubCapability === 'all') {
                const groupedSubChats = capability.selectedSubCapabilities.map((subCapability) => {
                    const subChats = chatHistory.filter(
                        (chat) =>
                            `${chat.capabilityId}` === `${selectedCapability}` &&
                            `${chat.subCapabilityId}` === `${subCapability.subCapabilityId}`
                    );
    
                    if (subChats.length > 0) {
                        return [
                            `Sub-Capability: ${subCapability.subCapability}`,
                            ...subChats.map(
                                (chat) =>
                                    `[${chat.senderName}] (${formatChatDate(new Date(chat.messageDate))}): ${chat.message}`
                            ),
                            '',
                        ].join('\n');
                    }
    
                    return '';
                });
    
                filteredChats = [
                    `Capability: ${capabilityName}`,
                    ...groupedSubChats.filter(Boolean),
                ].join('\n');
            } else {
                const subCapabilityName =
                    capability?.selectedSubCapabilities?.find(
                        (subCap) => `${subCap.subCapabilityId}` === `${selectedSubCapability}`
                    )?.subCapability || 'Unknown Sub-Capability';
    
                filteredChats = [
                    `Capability: ${capabilityName}`,
                    `Sub-Capability: ${subCapabilityName}`,
                    '',
                    ...chatHistory
                        .filter(
                            (chat) =>
                                `${chat.capabilityId}` === `${selectedCapability}` &&
                                `${chat.subCapabilityId}` === `${selectedSubCapability}`
                        )
                        .map(
                            (chat) =>
                                `[${chat.senderName}] (${formatChatDate(new Date(chat.messageDate))}): ${chat.message}`
                        ),
                ].join('\n\n');
            }
        }
    
        if (!filteredChats) {
            alert('No chats available for the selected filter.');
            return;
        }
    
        const blob = new Blob([filteredChats], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
    
        const a = document.createElement('a');
        a.href = url;
        a.download = 'chat-history.txt';
        a.click();
    
        URL.revokeObjectURL(url);
    };

    // Show all capabilities and subcapabilities regardless of chat history
    const getFilteredCapabilities = () => {
        if (!data || !data.data || !data.data.selectedCapabilities) return [];
        return data.data.selectedCapabilities;
    };
    
    const getFilteredSubCapabilities = () => {
        if (selectedCapability === 'all') return [];
        const capability = data?.data?.selectedCapabilities.find(
            (cap) => `${cap.capabilityId}` === `${selectedCapability}`
        );
        if (!capability) return [];
        return capability.selectedSubCapabilities;
    };

    const toggleSpeechRecognition = (subCapabilityId) => {
        if (!recognitionRef.current) {
            console.error("Speech Recognition is not initialized.");
            return;
        }

        if (isListening === subCapabilityId) {
            recognitionRef.current.stop();
            setIsListening(false);
            activeSubCapabilityRef.current = null;
        } else {
            if (recognitionRef.current && isListening) {
                recognitionRef.current.stop();
            }
            recognitionRef.current.start();
            setIsListening(subCapabilityId);
            activeSubCapabilityRef.current = subCapabilityId;
        }
    };

    const fetchAssessmentData = async (modal) => {
        try {
            const payload = {
                userId: userId,
                projectId: projectId,
            };

            const assessmentData = await getSubCapabilityAssessmentData(payload);
            const matchedSubCapability = assessmentData.data.find((item) =>
                sameAssessmentUnit(
                    item.capabilityId,
                    item.subCapabilityId,
                    modal.capabilityId,
                    modal.subCapabilityId
                )
            );

            if (matchedSubCapability) {
                setCurrentStatusDescription(matchedSubCapability.currentStatusDescription);
                setFutureStatusDescription(matchedSubCapability.futureStatusDescription);
                setCurrentState(["aware", "reactive", "proactive", "managed", "optimized"][matchedSubCapability.currentStatusRating - 1]);
                setFutureState(["aware", "reactive", "proactive", "managed", "optimized"][matchedSubCapability.futureStatusRating - 1]);
            } else {
                setCurrentStatusDescription('');
                setFutureStatusDescription('');
                setCurrentState('');
                setFutureState('');
            }
        } catch (error) {
            console.error('Error fetching assessment data:', error.message);
        }
    };

    const fetchSelectedCapabilitiesData = async () => {
        try {
            if (!userId || !projectId) return;
            const payload = {
                userId: userId,
                projectId: projectId,
            };
            const selectedCapabilitiesResponse = await fetchSelectedCapabilities(payload);
            setSelectedCapabilitiesData(selectedCapabilitiesResponse);
        } catch (error) {
            console.error("Error fetching selected capabilities:", error.message);
        }
    };

    const fetchChatHistory = async () => {
        if (!userId || !projectId) return;
        try {
            const payload = {
                userId: userId,
                projectId: projectId,
            };
            const chatData = await fetchChat(payload);
            const capabilitiesData = await getAssessmentDetails(payload);
            // const chatCountData = await getChatCount(payload); // Commented out for SME user

            // For SME user, we don't need chatCount, so just use the capabilities as is
            selectedCapabilitiesRef.current =
                capabilitiesData?.data?.selectedCapabilities ?? [];
            setData({ ...capabilitiesData });
            setChatHistory(chatData);

            // Highlight unread client threads; auto-open uses the most recent unread one
            const unreadActionable = getUnreadActionableMessages(
                chatData,
                userId,
                projectId,
                "sme"
            );

            if (unreadActionable.length > 0) {
                const latestUnread = unreadActionable[0];
                setLatestMessage({
                    capabilityId: latestUnread.capabilityId,
                    subCapabilityId: latestUnread.subCapabilityId,
                    messageDate: latestUnread.messageDate,
                });

                setExpandedCapability(latestUnread.capabilityId);
                setSelectedCard(latestUnread.capabilityId);
            } else {
                setLatestMessage(null);
            }
        } catch (error) {
            console.error("Error fetching chat history:", error.message);
        }
    };

    const fetchSuggestedQuestions = async () => {
        if (!userId || !projectId) return;
        setSuggestionsLoading(true);
        try {
            const result = await getSuggestedChatQuestions({
                userId,
                projectId,
                smeUserId,
            });
            applyInterviewSessionResult(result);
        } catch (error) {
            console.error("Error fetching suggested questions:", error.message);
        } finally {
            setSuggestionsLoading(false);
        }
    };

    const saveAutoFlag = async (next) => {
        setAuto(next);
        try {
            const saved = await setSmeChatAuto({
                userId,
                projectId,
                smeUserId,
                auto: next,
            });
            setAuto(saved === true);
            // Re-open interview session when leaving auto (suggester) or clearing AI ids when enabling auto.
            if (!saved) {
                await fetchSuggestedQuestions();
            } else {
                setConversationIds({});
                setSuggestedQuestions({});
                clearUnusedSuggestions();
            }
        } catch (error) {
            console.error("Error saving auto flag:", error.message);
        }
    };

    // Remove logic that disables input if SME hasn't sent a message
    // Always allow input
    const isInputDisabled = (capabilityId, subCapabilityId) => {
        return false;
    };

    useEffect(() => {
        // Remove logic that disables input based on SME message
        setIsDisabled(false);
    }, [chatHistory]);

    const copySuggestedQuestion = (capabilityId, subCapabilityId, questionText) => {
        const storageKey = threadStorageKey(capabilityId, subCapabilityId);
        const question =
            questionText || getSuggestionsForThread(capabilityId, subCapabilityId)[0];
        if (!question) {
            return;
        }

        setMessages((prev) => ({
            ...prev,
            [storageKey]: question,
        }));
        setUsedSuggestions((prev) => ({
            ...prev,
            [storageKey]: dedupeSuggestions([...(prev[storageKey] || []), question]),
        }));
        setSuggestionSources((prev) => ({ ...prev, [storageKey]: true }));

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(question).catch(() => {});
        }

        composerRef.current?.focus();
    };

    const handleUseSuggestion = (question) => {
        if (!activeModal) {
            return;
        }
        copySuggestedQuestion(activeModal.capabilityId, activeModal.subCapabilityId, question);
    };

    const formatMessageTime = (value) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return '';
        }
        const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
        const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        return `${day}, ${time}`;
    };

    const activeModal = showModals[0] ?? null;
    const activeThreadStorageKey = activeModal
        ? threadStorageKey(activeModal.capabilityId, activeModal.subCapabilityId)
        : null;
    const activeSuggestions = activeModal
        ? getSuggestionsForThread(activeModal.capabilityId, activeModal.subCapabilityId)
        : [];

    const isWaitingForClientReply = useMemo(() => {
        if (!activeModal) {
            return false;
        }
        const latest = (chatHistory || [])
            .filter(
                (chat) =>
                    chat.id &&
                    sameAssessmentUnit(
                        chat.capabilityId,
                        chat.subCapabilityId,
                        activeModal.capabilityId,
                        activeModal.subCapabilityId
                    )
            )
            .sort((a, b) => b.id - a.id)[0];
        return latest?.senderName === "SME";
    }, [activeModal, chatHistory]);

    // Baseline once per thread when the AI session opens — do not re-run on every chat poll
    // or we mark new client messages as "already synced" before the suggester refresh runs.
    useEffect(() => {
        if (auto !== false) {
            return;
        }

        Object.keys(conversationIds).forEach((storageKey) => {
            const parsed = parseAssessmentUnitKey(storageKey);
            if (parsed?.capabilityId != null) {
                ensureSyncedClientBaseline(
                    parsed.capabilityId,
                    parsed.subCapabilityId,
                    chatHistory
                );
            }
        });
    }, [auto, conversationIds]);

    // When a chat is opened, load suggestions if not already provided by open.
    useEffect(() => {
        if (auto !== false || readOnly || activeModal == null || suggestionsLoading) {
            setSuggestionPanelLoading(false);
            return;
        }

        const activeKey = activeThreadStorageKey;
        const conversationId = activeKey ? conversationIds[activeKey] : null;
        if (!conversationId || !activeKey) {
            setSuggestionPanelLoading(false);
            return;
        }

        if (
            getSuggestionsForThread(activeModal.capabilityId, activeModal.subCapabilityId).length >
            0
        ) {
            ensureSyncedClientBaseline(
                activeModal.capabilityId,
                activeModal.subCapabilityId,
                chatHistory
            );
            setSuggestionPanelLoading(false);
            return;
        }

        if (suggestionAttemptedRef.current[activeKey]) {
            setSuggestionPanelLoading(false);
            return;
        }

        const threadMessages = (chatHistory || []).filter(
            (chat) =>
                chat.id &&
                sameAssessmentUnit(
                    chat.capabilityId,
                    chat.subCapabilityId,
                    activeModal.capabilityId,
                    activeModal.subCapabilityId
                )
        );
        const latest = [...threadMessages].sort((a, b) => b.id - a.id)[0];
        const latestIsSme = latest?.senderName === "SME";
        const latestIsClient =
            latest &&
            !latestIsSme &&
            String(latest.message || "").trim().length > 0;

        // SME spoke last — no suggestion until the client replies.
        if (latestIsSme) {
            suggestionAttemptedRef.current[activeKey] = true;
            setSuggestedQuestions((prev) => ({ ...prev, [activeKey]: [] }));
            setSuggestionPanelLoading(false);
            return;
        }

        let cancelled = false;
        suggestionAttemptedRef.current[activeKey] = true;
        setSuggestionPanelLoading(true);

        (async () => {
            try {
                const modal = showModals[0];
                let suggestions = [];

                if (latestIsClient) {
                    syncedClientMessageIdsRef.current[activeKey] = latest.id;
                    const replyResult = await submitInterviewClientReply({
                        conversationId,
                        content: latest.message,
                        capabilityId: modal?.capabilityId ?? latest.capabilityId,
                        subCapabilityId: chatApiSubCapabilityId(activeModal.subCapabilityId),
                    });
                    suggestions = normalizeSuggestionsFromApi(replyResult);
                } else {
                    const refreshed = await refreshInterviewSuggestion({
                        conversationId,
                        capabilityId: modal?.capabilityId,
                        subCapabilityId: chatApiSubCapabilityId(activeModal.subCapabilityId),
                    });
                    suggestions = normalizeSuggestionsFromApi(refreshed);
                }

                if (syncedClientMessageIdsRef.current[activeKey] == null) {
                    const maxId = threadMessages.reduce((max, chat) => Math.max(max, chat.id || 0), 0);
                    syncedClientMessageIdsRef.current[activeKey] = maxId;
                }

                if (!cancelled) {
                    const merged = mergeWithStoredUnused(
                        activeModal.capabilityId,
                        activeModal.subCapabilityId,
                        suggestions
                    );
                    setUnusedForSubCap(
                        activeModal.capabilityId,
                        activeModal.subCapabilityId,
                        merged
                    );
                    setSuggestedQuestions((prev) => ({
                        ...prev,
                        [activeKey]: merged,
                    }));
                }
            } catch (error) {
                console.error("Error loading chat suggestion:", error?.message || error);
                suggestionAttemptedRef.current[activeKey] = false;
            } finally {
                if (!cancelled) {
                    setSuggestionPanelLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        activeModal,
        activeThreadStorageKey,
        conversationIds,
        suggestedQuestions,
        auto,
        readOnly,
        suggestionsLoading,
        chatHistory,
        showModals,
    ]);

    // Suggester: when a new client message arrives (any thread), fetch AI suggestions automatically.
    useEffect(() => {
        if (auto !== false || readOnly || suggestionsLoading) {
            return;
        }

        const subCapabilityIds = Object.keys(conversationIds);
        if (!subCapabilityIds.length) {
            return;
        }

        subCapabilityIds.forEach((storageKey) => {
            const subCapKey = storageKey;
            const conversationId = conversationIds[subCapKey];
            if (!conversationId) {
                return;
            }

            const parsed = parseAssessmentUnitKey(storageKey);
            const latestClient = parsed?.capabilityId != null
                ? getLatestClientMessageForThread(
                    parsed.capabilityId,
                    parsed.subCapabilityId,
                    chatHistory
                  )
                : (chatHistory || [])
                    .filter(
                        (chat) =>
                            chat.senderName !== "SME" &&
                            chat.id &&
                            `${chat.subCapabilityId}` === `${parsed?.subCapabilityId ?? ""}`
                    )
                    .sort((a, b) => b.id - a.id)[0];
            if (!latestClient?.id) {
                return;
            }

            const lastSynced = syncedClientMessageIdsRef.current[subCapKey];
            if (lastSynced != null && latestClient.id <= lastSynced) {
                return;
            }

            if (clientReplySyncInFlightRef.current[subCapKey]) {
                return;
            }

            const previousSynced = lastSynced;
            syncedClientMessageIdsRef.current[subCapKey] = latestClient.id;
            clientReplySyncInFlightRef.current[subCapKey] = true;

            if (activeModal && storageKey === activeThreadStorageKey) {
                setSuggestionPanelLoading(true);
            }

            void (async () => {
                try {
                    const result = await submitInterviewClientReply({
                        conversationId,
                        content: latestClient.message,
                        capabilityId: latestClient.capabilityId,
                        subCapabilityId: chatApiSubCapabilityId(latestClient.subCapabilityId),
                    });

                    const nextSuggestions = mergeWithStoredUnused(
                        latestClient.capabilityId,
                        latestClient.subCapabilityId,
                        normalizeSuggestionsFromApi(result)
                    );

                    setUnusedForSubCap(
                        latestClient.capabilityId,
                        latestClient.subCapabilityId,
                        nextSuggestions
                    );
                    setSuggestedQuestions((prev) => ({
                        ...prev,
                        [subCapKey]: nextSuggestions,
                    }));
                    suggestionAttemptedRef.current[subCapKey] = true;
                } catch (error) {
                    console.error(
                        "Error refreshing suggestions after client reply:",
                        error?.message || error
                    );
                    if (previousSynced != null) {
                        syncedClientMessageIdsRef.current[subCapKey] = previousSynced;
                    } else {
                        delete syncedClientMessageIdsRef.current[subCapKey];
                    }
                } finally {
                    clientReplySyncInFlightRef.current[subCapKey] = false;
                    if (activeModal && storageKey === activeThreadStorageKey) {
                        setSuggestionPanelLoading(false);
                    }
                }
            })();
        });
    }, [
        chatHistory,
        conversationIds,
        auto,
        readOnly,
        suggestionsLoading,
        activeModal,
        activeThreadStorageKey,
    ]);

    const handleFileChange = (subCapabilityId, e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFilesBySubCapability(prev => ({ ...prev, [subCapabilityId]: selectedFile }));
        }
    };

    // Modified sendMessage to include userName: "SME" in the payload to handleSendMessage
    const sendMessage = async (capabilityId, subCapabilityId) => {
        if (readOnly) {
            return;
        }

        const storageKey = threadStorageKey(capabilityId, subCapabilityId);

        try {
            const content = (messages[storageKey] || "").trim();
            if (!content) {
                alert("Please fill the response.");
                return;
            }
            setIsDisabled(true);
            await handleSendMessage(
                userId,
                capabilityId,
                subCapabilityId,
                projectId,
                content,
                filesBySubCapability[storageKey],
                null,
                null,
                null,
                "SME"
            );

            // Keep interview AI in sync (suggester only).
            const subCapKey = storageKey;
            const conversationId = conversationIds[subCapKey];
            if (auto === false && conversationId) {
                await sendInterviewQuestion({ conversationId, content });
                const remaining = stashUnusedSuggestions(
                    capabilityId,
                    subCapabilityId,
                    getSuggestionsForThread(capabilityId, subCapabilityId),
                    content
                );
                setSuggestedQuestions((prev) => ({
                    ...prev,
                    [subCapKey]: remaining,
                }));
                if (!remaining.length) {
                    suggestionAttemptedRef.current[subCapKey] = true;
                }
            }

            setMessages((prev) => ({ ...prev, [storageKey]: "" }));
            setSuggestionSources((prev) => ({ ...prev, [storageKey]: false }));
            setFilesBySubCapability((prev) => ({ ...prev, [storageKey]: null }));
            
            // Clear latest message if SME responded to the latest user message
            if (latestMessage && 
                latestMessage.capabilityId === capabilityId && 
                latestMessage.subCapabilityId === subCapabilityId) {
                setLatestMessage(null);
            }
            
            fetchChatHistory();
        } catch (error) {
            console.error("Error sending message:", error.message);
            alert(error?.message || "Failed to send message. Please try again.");
        } finally {
            setIsDisabled(false);
        }
    };

    const handleMessageKeyDown = (event, capabilityId, subCapabilityId) => {
        if (readOnly || event.key !== "Enter" || event.shiftKey) {
            return;
        }

        event.preventDefault();
        if (!isDisabled) {
            sendMessage(capabilityId, subCapabilityId);
        }
    };

    const handleDownload = async (filename) => {
        try {
            await downloadChatFile(filename);
        } catch (error) {
            console.error("Error downloading file:", error.message);
        }
    };

    const getFileIcon = (filename) => {
        const extension = filename.split(".").pop().toLowerCase();
        switch (extension) {
            case "pdf":
                return basename+"/images/pdf.webp";
            case "docx":
                return basename+"/images/docx.webp";
            case "png":
                return basename+"/images/png-file-.webp";
            case "jpg":
                return basename+"/images/jpg.webp";
            default:
                return basename+"/images/file.webp";
        }
    };

    // Always allow expanding/clicking any capability/subcapability
    const handleCapabilityInteraction = (capabilityId, subCapabilityId = null, subCapabilityName = null) => {
        if (subCapabilityId == null) {
            setExpandedCapability((prev) => (prev === capabilityId ? null : capabilityId));
            setSelectedCard((prev) => (prev === capabilityId ? null : capabilityId));
            setShowModals([]);
            return;
        }

        const threadSubId = chatApiSubCapabilityId(subCapabilityId);

        userOpenedChatRef.current = true;
        if (autoOpenTimerRef.current) {
            clearTimeout(autoOpenTimerRef.current);
            autoOpenTimerRef.current = null;
        }
    
        // Always show the modal for the selected subcapability, even if no chat exists
        setShowModals([
            {
                capabilityId,
                subCapabilityId: threadSubId,
                subCapability: subCapabilityName,
            },
        ]);
        setSelectedCard(capabilityId);
        setSubSelectedCard(threadSubId);
        setSelectedSubCapability({
            capabilityId,
            subCapabilityId: threadSubId,
            subCapability: subCapabilityName,
        });
    
        setShowModal(true);
    };

    const formatChatDate = (date) => {
        const options = { month: 'short', day: '2-digit', year: 'numeric' };
        const datePart = new Intl.DateTimeFormat('en-US', options).format(date);
        const timePart = date.toLocaleTimeString('en-US');
        return `${datePart}, ${timePart}`;
    };

    const openModal = async (modal) => {
        setSelectedSubCapability(modal);
        await fetchAssessmentData(modal);
        setShowAssessmentModal(true);
    };

    const closeModal = () => {
        setShowAssessmentModal(false);
    };

    // Show appropriate message when no project/user is provided
    if (!project && !currentUserId) {
        return (
            <div className="chat-main-container">
                <div className="chat-frame-parents"></div>
                <div className="chat-frame-parent">
                    <div className="chat-message-section">
                        Please select a project to view chat details.
                        <p>Project: {project?.projectName || 'No project selected'}</p>
                    </div>
                </div>
            </div>
        );
    }

const hasCapabilities = !!data?.data?.selectedCapabilities?.length;
const showSuggestionPanel = !readOnly && auto === false && isChatModuleReady;
const activeThreadMessages = activeModal
    ? getThreadMessagesNewestFirst(
          chatHistory,
          activeModal.capabilityId,
          activeModal.subCapabilityId
      ).slice().reverse()
    : [];
const activeMessageText = activeThreadStorageKey ? messages[activeThreadStorageKey] || '' : '';
const composerFromSuggestion =
    !!activeThreadStorageKey && !!suggestionSources[activeThreadStorageKey] && activeMessageText.trim() !== '';

return (
      <div className={`smechat${showSuggestionPanel ? " smechat--with-aside" : ""}`}>
        <nav className="smechat-nav" aria-label="Conversations">
          <div className="smechat-nav__head">
            <p className="smechat-nav__label">Conversations</p>
            {hasCapabilities ? (
              <span className="smechat-nav__count">{data.data.selectedCapabilities.length}</span>
            ) : null}
          </div>
          <div className="smechat-nav__list">
            {!isChatModuleReady ? (
              <ContentLoader compact label="Loading chats…" />
            ) : !hasCapabilities ? (
              <p className="smechat-nav__empty">No chat available</p>
            ) : (
              data.data.selectedCapabilities.map((capability) => {
                const leaf = isLeafSelectedCapability(capability);
                const firstUnit = capability.selectedSubCapabilities?.[0];
                const activeUnitKey =
                  selectedCard === capability.capabilityId && subSelectedCard != null
                    ? assessmentUnitKey(capability.capabilityId, subSelectedCard)
                    : null;
                const isThreadActive = isCapabilityNavHighlighted(capability.capabilityId, {
                  leaf,
                  firstSubCapabilityId: firstUnit?.subCapabilityId,
                  selectedCard,
                  subSelectedCard: activeUnitKey,
                  activeUnitKey,
                });
                const hasUnread = hasUnreadForCapability(unreadThreads, capability.capabilityId);
                const isExpanded = !leaf && expandedCapability === capability.capabilityId;
                return (
                  <div key={capability.capabilityId} className="smechat-nav__group">
                    <button
                      type="button"
                      className={`smechat-nav__item${isThreadActive ? " is-active" : ""}`}
                      aria-expanded={leaf ? undefined : isExpanded}
                      onClick={() => {
                        if (leaf && firstUnit) {
                          handleCapabilityInteraction(
                            capability.capabilityId,
                            chatApiSubCapabilityId(firstUnit.subCapabilityId),
                            firstUnit.subCapability || capability.capability
                          );
                          return;
                        }
                        handleCapabilityInteraction(capability.capabilityId);
                      }}
                    >
                      <span
                        className={`smechat-nav__dot${hasUnread ? " is-unread" : ""}`}
                        aria-hidden="true"
                      />
                      <span className="smechat-nav__text">
                        <span className="smechat-nav__title">{capability.capability}</span>
                        {!leaf ? (
                          <span className="smechat-nav__sub">
                            {capability.selectedSubCapabilities.length} sub-capabilities
                          </span>
                        ) : null}
                      </span>
                      {readStateVersion >= 0 && hasUnread ? (
                        <span className="smechat-nav__badge" aria-label="New message">New</span>
                      ) : null}
                      {!leaf ? (
                        <span
                          className={`smechat-nav__chev${isExpanded ? " is-open" : ""}`}
                          aria-hidden="true"
                        />
                      ) : null}
                    </button>
                    {isExpanded ? (
                      <div
                        ref={(el) => (dropdownRefs.current[capability.capabilityId] = el)}
                        className="smechat-nav__subs"
                      >
                        {capability.selectedSubCapabilities.map((subCapability) => {
                          const isSubActive = sameAssessmentUnit(
                            capability.capabilityId,
                            subSelectedCard,
                            capability.capabilityId,
                            subCapability.subCapabilityId
                          );
                          const subUnread = hasUnreadForSubCapability(
                            unreadThreads,
                            capability.capabilityId,
                            subCapability.subCapabilityId
                          );
                          return (
                            <button
                              type="button"
                              key={assessmentUnitKey(
                                capability.capabilityId,
                                subCapability.subCapabilityId
                              )}
                              className={`smechat-nav__sub-item${isSubActive ? " is-active" : ""}`}
                              onClick={() =>
                                handleCapabilityInteraction(
                                  capability.capabilityId,
                                  chatApiSubCapabilityId(subCapability.subCapabilityId),
                                  subCapability.subCapability
                                )
                              }
                            >
                              <span
                                className={`smechat-nav__dot${subUnread ? " is-unread" : ""}`}
                                aria-hidden="true"
                              />
                              <span className="smechat-nav__sub-label">
                                {subCapability.subCapability}
                              </span>
                              {readStateVersion >= 0 && subUnread ? (
                                <span className="smechat-nav__badge" aria-label="New message">New</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </nav>

        <section className="smechat-main">
          {!showModal || !activeModal ? (
            <div className="smechat-empty" role="status">
              <span className="smechat-empty__icon" aria-hidden="true" />
              <p className="smechat-empty__title">
                {isChatModuleReady && !hasCapabilities ? "No chat available" : "Select a conversation"}
              </p>
              {isChatModuleReady && hasCapabilities ? (
                <p className="smechat-empty__text">
                  Choose a capability or sub-capability from the list to view messages and respond.
                </p>
              ) : null}
            </div>
          ) : (
            <>
              <header className="smechat-main__head">
                <h2 className="smechat-main__title">{activeModal.subCapability}</h2>
                <div className="smechat-main__actions">
                  {!readOnly && isChatModuleReady ? (
                    <>
                      {auto === true ? (
                        <span className="smechat-main__note">AI is responding automatically.</span>
                      ) : null}
                      <Switch
                        checked={auto === true}
                        disabled={suggestionsLoading || auto === null}
                        label="Auto-reply"
                        onCheckedChange={(checked) => {
                          saveAutoFlag(checked);
                        }}
                      />
                    </>
                  ) : null}
                  <a
                    href="#"
                    className="smechat-main__link"
                    onClick={(e) => {
                      e.preventDefault();
                      openModal(activeModal);
                    }}
                  >
                    View assessment
                  </a>
                </div>
              </header>

              <div className="smechat-thread" ref={threadBodyRef} onScroll={handleThreadBodyScroll}>
                {activeThreadMessages.length === 0 ? (
                  <p className="smechat-thread__empty">No messages yet in this conversation.</p>
                ) : null}
                {activeThreadMessages.map((chat, index) => {
                  const fromSme = chat.senderName === "SME";
                  return (
                    <article
                      key={chat.id || index}
                      className={`smechat-msg ${fromSme ? "smechat-msg--sme" : "smechat-msg--client"}`}
                    >
                      <div className="smechat-msg__meta">
                        <span className="smechat-msg__sender">
                          {fromSme && !readOnly ? "You · SME" : chat.senderName}
                        </span>
                        <time className="smechat-msg__time">{formatMessageTime(chat.messageDate)}</time>
                      </div>
                      <div className="smechat-msg__bubble">{chat.message}</div>
                      {chat.attachments && chat.attachments.length > 0 ? (
                        <div className="smechat-msg__files">
                          {chat.attachments.map((attachment, fileIndex) => (
                            <button
                              type="button"
                              key={fileIndex}
                              className="smechat-file"
                              onClick={() => handleDownload(attachment.attachmentUrl.split('/').pop())}
                            >
                              <img
                                src={getFileIcon(attachment.attachmentUrl)}
                                alt=""
                                className="smechat-file__icon"
                              />
                              <span className="smechat-file__name">
                                {attachment.attachmentUrl.split('/').pop()}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>

              {readOnly ? (
                <div className="smechat-composer smechat-composer--readonly" data-element="message-input-readonly">
                  View only — you cannot participate in this chat.
                </div>
              ) : (
                <div className="smechat-composer" data-element="message-input">
                  <div className="smechat-composer__box">
                    {composerFromSuggestion ? (
                      <span className="smechat-composer__source">
                        ✦ From AI suggestion · edit before sending
                      </span>
                    ) : null}
                    <textarea
                      ref={composerRef}
                      className="smechat-composer__input"
                      placeholder="Your response"
                      rows={2}
                      value={activeMessageText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setMessages((prev) => ({ ...prev, [activeThreadStorageKey]: value }));
                      }}
                      onKeyDown={(e) =>
                        handleMessageKeyDown(e, activeModal.capabilityId, activeModal.subCapabilityId)
                      }
                      disabled={isDisabled}
                    />
                    {filesBySubCapability[activeThreadStorageKey] ? (
                      <div className="smechat-composer__attached">
                        <span>Attached: {filesBySubCapability[activeThreadStorageKey].name}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setFilesBySubCapability((prev) => ({ ...prev, [activeThreadStorageKey]: null }));
                            const input = document.getElementById(`file-upload-${activeThreadStorageKey}`) as HTMLInputElement | null;
                            if (input) {
                              input.value = '';
                            }
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : null}
                    <div className="smechat-composer__bar">
                      <div className="smechat-composer__tools">
                        <button
                          type="button"
                          className="smechat-icon-btn"
                          aria-label="Voice input"
                          onClick={() => toggleSpeechRecognition(activeThreadStorageKey)}
                        >
                          <img
                            src={`${basename}/images/mic.svg`}
                            alt=""
                            className={isListening === activeThreadStorageKey ? "is-listening" : ""}
                          />
                        </button>
                        <input
                          type="file"
                          className="smechat-composer__file"
                          onChange={(e) => handleFileChange(activeThreadStorageKey, e)}
                          id={`file-upload-${activeThreadStorageKey}`}
                        />
                        <label
                          htmlFor={`file-upload-${activeThreadStorageKey}`}
                          className="smechat-icon-btn"
                          aria-label="Attach file"
                        >
                          <img src={`${basename}/images/Vector.svg`} alt="" />
                        </label>
                      </div>
                      <button
                        type="button"
                        className="smechat-send"
                        onClick={() => sendMessage(activeModal.capabilityId, activeModal.subCapabilityId)}
                        disabled={isDisabled}
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        {showSuggestionPanel ? (
          <SmeChatSuggestionPanel
            suggestions={showModal && activeModal ? activeSuggestions : []}
            usedSuggestions={activeThreadStorageKey ? usedSuggestions[activeThreadStorageKey] || [] : []}
            loading={!!activeModal && (suggestionsLoading || suggestionPanelLoading)}
            waitingForClient={!!activeModal && isWaitingForClientReply}
            disabled={isDisabled || !activeModal}
            composerText={activeMessageText}
            onUse={handleUseSuggestion}
          />
        ) : null}
                          {showAssessmentModal && (
                              <div className="modal-overlay" onClick={closeModal}>
                                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                      <div className="asses-sub-capability-dataai-vision-parent">
                                          <div className="asses-sub-capability-header">
                                              <div className="asses-strategic-management">
                                                  <h5>Sub Capability: {selectedSubCapability?.subCapability}</h5>
                                              </div>
                                          </div>
                                          <div className="asses-vector-parent">
                                              <img
                                                  className="asses-vector-icon"
                                                  alt=""
                                                  src={`${basename}/images/Vector 2.svg`}
                                              />
                                              <div className="asses-frame-parent10">
                                                  <div className="asses-frame-parent11">
                                                      <div className="asses-current-state-parent">
                                                          <div className="asses-strategic-management required">Current State</div>
                                                          <div className="asses-describe-the-current">
                                                              Describe the current state of your organization as you perceive it.
                                                          </div>
                                                      </div>
                                                      {["aware", "reactive", "proactive", "managed", "optimized"].map(
                                                          (state) => (
                                                              <div key={state} style={{ marginBottom: "10px" }}>
                                                                  <input
                                                                      type="radio"
                                                                      id={`current-${state}`}
                                                                      name="currentState"
                                                                      value={state}
                                                                      checked={currentState === state}
                                                                      disabled
                                                                  />
                                                                  <label htmlFor={`current-${state}`} style={{ marginLeft: "8px" }}>
                                                                      {state.charAt(0).toUpperCase() + state.slice(1)}
                                                                  </label>
                                                              </div>
                                                          )
                                                      )}
                                                      <div className="asses-frame-wrapper13">
                                                          <textarea
                                                              className="asses-custom-textarea"
                                                              placeholder="Current State Comment"
                                                              value={currentStatusDescription}
                                                              disabled
                                                          />
                                                      </div>
                                                  </div>
                                                  <img alt="" src={`${basename}/images/VerticalVector2.svg`} />
                                                  <div className="asses-frame-parent11">
                                                      <div className="asses-current-state-parent">
                                                          <div className="asses-strategic-management required">Future State</div>
                                                          <div className="asses-describe-the-current">
                                                              Describe the future state you envision for your organization.
                                                          </div>
                                                      </div>
                                                      {["aware", "reactive", "proactive", "managed", "optimized"].map(
                                                          (state) => (
                                                              <div key={state} style={{ marginBottom: "10px" }}>
                                                                  <input
                                                                      type="radio"
                                                                      id={`future-${state}`}
                                                                      name="futureState"
                                                                      value={state}
                                                                      checked={futureState === state}
                                                                      disabled
                                                                  />
                                                                  <label htmlFor={`future-${state}`} style={{ marginLeft: "8px" }}>
                                                                      {state.charAt(0).toUpperCase() + state.slice(1)}
                                                                  </label>
                                                              </div>
                                                          )
                                                      )}
                                                      <div className="asses-frame-wrapper13">
                                                          <textarea
                                                              className="asses-custom-textarea"
                                                              placeholder="Future State Comment"
                                                              value={futureStatusDescription}
                                                              disabled
                                                          />
                                                      </div>
                                                  </div>
                                              </div>
                                          </div>
                                          <div className="asses-frame-parent15">
                                              <button className="close-button" onClick={closeModal}>
                                                  <img src={`${basename}/images/close.webp`} alt="Arrow Icon" style={{width:'20px', height:'20px'}}></img>
                                              </button>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          )}
      </div>
);
};

export default Chat;
